import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AllergyManagement from '../AllergyManagement';
import { AllergyApi, PatientAllergy } from '../allergies';
import AppointmentList, { getAppointmentReason, partitionAppointments } from '../AppointmentList';
import type { Appointment, Doctor } from '../components/calendar/types';
import { API_CONFIG, getApiUrl, getAuthHeaders } from '../config/api';
import MedicalStoryList from '../MedicalStoryList';
import PatientFamilyLinksPanel from '../PatientFamilyLinksPanel';
import PatientPhotoGallery from '../PatientPhotoGallery';
import PatientFinancePanel from '../components/finance/PatientFinancePanel';
import TreatmentsPanel from '../TreatmentsPanel';
import { buildDisplayName, PatientBasic } from '../types';
import { clearPatientsCache } from '../utils/referenceDataCache';
import { useThemedStyles } from '../theme/useThemedStyles';
import { formatDate, formatDateTime as formatDisplayDateTime } from '../utils/dateFormat';
import './PatientProfile.css';

type TabId = 'overview' | 'appointments' | 'treatments' | 'clinical' | 'finance' | 'more';

interface PatientProfileData extends PatientBasic {
  mainDoctorId?: number | null;
  hasCriticalAllergy?: boolean;
}

export interface TreatmentSummary {
  id: number;
  name: string;
  status: string;
  totalCost: number;
  totalPaid: number;
  totalDue: number;
  createdAt: string;
}

export interface MedicalStorySummary {
  id: number;
  date: string;
  description: string;
  appointmentId?: number | null;
}

export interface OverviewData {
  appointments: Appointment[];
  treatments: TreatmentSummary[];
  stories: MedicalStorySummary[];
  allergies: PatientAllergy[];
}

const emptyOverview: OverviewData = {
  appointments: [],
  treatments: [],
  stories: [],
  allergies: []
};

const dangerOrder: Record<string, number> = {
  Critical: 4,
  High: 3,
  Moderate: 2,
  Low: 1
};

const statusColors: Record<string, { background: string; color: string }> = {
  New: { background: '#1e3a8a', color: '#bfdbfe' },
  Active: { background: '#14532d', color: '#bbf7d0' },
  Inactive: { background: '#7c2d12', color: '#fed7aa' },
  Archived: { background: '#3f3f46', color: '#e4e4e7' },
  Responsible: { background: '#581c87', color: '#e9d5ff' }
};

const calculateAge = (dateOfBirth?: string) => {
  if (!dateOfBirth) return null;
  const birth = new Date(dateOfBirth);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const month = now.getMonth() - birth.getMonth();
  if (month < 0 || (month === 0 && now.getDate() < birth.getDate())) age -= 1;
  return age;
};

const profilePictureUrl = (filePath: string) => {
  const normalizedPath = filePath.replace(/\\/g, '/').replace(/^\/+/, '');
  return `${API_CONFIG.BASE_URL.replace(/\/+$/, '')}/${normalizedPath}`;
};

export const derivePatientOverview = (overview: OverviewData, now = new Date()) => {
  const validAppointments = overview.appointments.filter(item => item.status !== 'canceled');
  const groups = partitionAppointments(validAppointments, now);
  const activeTreatments = overview.treatments
    .filter(item => !['completed', 'archived'].includes(item.status?.toLowerCase()))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const allergies = [...overview.allergies].sort(
    (a, b) => (dangerOrder[b.dangerLevel] || 0) - (dangerOrder[a.dangerLevel] || 0)
  );
  const recentStories = [...overview.stories]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 3);
  const closestFinancialSummary = [...validAppointments]
    .filter(item => item.financialSummary)
    .sort((a, b) => Math.abs(new Date(a.startTime || 0).getTime() - now.getTime())
      - Math.abs(new Date(b.startTime || 0).getTime() - now.getTime()))[0]?.financialSummary;
  return {
    nextAppointment: groups.future[0] || null,
    lastVisit: groups.past.find(item => item.startTime) || null,
    activeTreatments,
    allergies,
    recentStories,
    pastDue: closestFinancialSummary?.pastDue ?? 0,
    balanceDue: closestFinancialSummary?.patientBalanceDue
      ?? overview.treatments.reduce((sum, treatment) => sum + (treatment.totalDue || 0), 0)
  };
};

export default function PatientProfile({ id, token, onBack }: { id: number; token: string; onBack: () => void }) {
  const { t } = useTranslation();
  const theme = useThemedStyles();
  const [patient, setPatient] = useState<PatientProfileData | null>(null);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [overview, setOverview] = useState<OverviewData>(emptyOverview);
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [loading, setLoading] = useState(true);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [error, setError] = useState('');
  const [overviewWarnings, setOverviewWarnings] = useState<string[]>([]);
  const [hcDraft, setHcDraft] = useState('');
  const [hcSaving, setHcSaving] = useState(false);
  const [hcMessage, setHcMessage] = useState('');

  const loadPatient = useCallback(async () => {
    const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.PATIENTS}/${id}`), {
      headers: getAuthHeaders(token)
    });
    if (response.status === 404) throw new Error(t('patientProfile.notFound'));
    if (!response.ok) throw new Error(t('patientProfile.error'));
    const data = await response.json();
    setPatient(data);
    setHcDraft(data.hc ?? '');
  }, [id, token, t]);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    const requests = [
      fetch(getApiUrl(API_CONFIG.ENDPOINTS.PATIENT_APPOINTMENTS, { patientId: id }), { headers: getAuthHeaders(token) }).then(response => {
        if (!response.ok) throw new Error(t('patientProfile.load.appointments'));
        return response.json();
      }),
      fetch(getApiUrl(`/patients/${id}/treatments`), { headers: getAuthHeaders(token) }).then(response => {
        if (!response.ok) throw new Error(t('patientProfile.load.treatments'));
        return response.json();
      }),
      fetch(getApiUrl(API_CONFIG.ENDPOINTS.PATIENT_MEDICAL_STORIES, { patientId: id }), { headers: getAuthHeaders(token) }).then(response => {
        if (!response.ok) throw new Error(t('patientProfile.load.notes'));
        return response.json();
      }),
      AllergyApi.listForPatient(token, id)
    ] as const;
    const results = await Promise.allSettled(requests);
    const warnings: string[] = [];
    const readResult = <T,>(index: number, fallback: T): T => {
      const result = results[index];
      if (result.status === 'fulfilled') return (Array.isArray(result.value) ? result.value : fallback) as T;
      warnings.push(result.reason instanceof Error ? result.reason.message : t('patientProfile.load.section'));
      return fallback;
    };
    setOverview({
      appointments: readResult<Appointment[]>(0, []),
      treatments: readResult<TreatmentSummary[]>(1, []),
      stories: readResult<MedicalStorySummary[]>(2, []),
      allergies: readResult<PatientAllergy[]>(3, [])
    });
    setOverviewWarnings(warnings);
    setOverviewLoading(false);
  }, [id, token, t]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    Promise.all([
      loadPatient(),
      fetch(getApiUrl(API_CONFIG.ENDPOINTS.DOCTORS), { headers: getAuthHeaders(token) })
        .then(response => response.ok ? response.json() : [])
        .then(data => {
          if (active) setDoctors(Array.isArray(data) ? data : []);
        })
    ])
      .catch(requestError => {
        if (active) setError(requestError instanceof Error ? requestError.message : t('patientProfile.error'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    loadOverview();
    return () => { active = false; };
  }, [loadOverview, loadPatient, token, t]);

  const handleMainDoctorChange = async (doctorId: string) => {
    const endpoint = doctorId
      ? `${API_CONFIG.ENDPOINTS.PATIENTS}/${id}/main-doctor/${doctorId}`
      : `${API_CONFIG.ENDPOINTS.PATIENTS}/${id}/main-doctor`;
    const response = await fetch(getApiUrl(endpoint), {
      method: doctorId ? 'PUT' : 'DELETE',
      headers: getAuthHeaders(token)
    });
    if (!response.ok) {
      setError(t('patientProfile.mainDoctorError'));
      return;
    }
    await loadPatient();
  };

  const handleHcSave = async () => {
    setHcSaving(true);
    setHcMessage('');
    try {
      const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.PATIENTS}/${id}/hc`), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ hc: hcDraft.trim() || null })
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || t('patientProfile.hc.saveError'));
      }
      const data = await response.json();
      setPatient(current => current ? { ...current, hc: data.hc ?? undefined } : current);
      setHcDraft(data.hc ?? '');
      clearPatientsCache();
      setHcMessage(t('patientProfile.hc.saved'));
    } catch (requestError) {
      setHcMessage(requestError instanceof Error ? requestError.message : t('patientProfile.hc.saveError'));
    } finally {
      setHcSaving(false);
    }
  };

  const derived = useMemo(() => derivePatientOverview(overview), [overview]);

  if (loading) return <div style={{ color: theme.text }}>{t('patientProfile.loading')}</div>;
  if (error && !patient) return <div style={{ color: '#ef4444' }}>{error}</div>;
  if (!patient) return <div style={{ color: theme.text }}>{t('patientProfile.notFound')}</div>;

  const displayName = buildDisplayName(patient);
  const age = calculateAge(patient.dateOfBirth);
  const statusStyle = statusColors[patient.status] || { background: '#334155', color: '#f1f5f9' };
  const hasCriticalAllergy = patient.hasCriticalAllergy || derived.allergies.some(item => item.dangerLevel === 'Critical');
  const mainDoctor = doctors.find(doctor => doctor.id === patient.mainDoctorId);
  const formatDateTime = (value?: string | null) => formatDisplayDateTime(value);
  const tabLabels: Array<{ id: TabId; label: string }> = [
    { id: 'overview', label: t('patientProfile.tabs.overview') },
    { id: 'appointments', label: t('patientProfile.tabs.appointments') },
    { id: 'treatments', label: t('patientProfile.tabs.treatments') },
    { id: 'clinical', label: t('patientProfile.tabs.clinical') },
    { id: 'finance', label: t('patientProfile.tabs.finance') },
    { id: 'more', label: t('patientProfile.tabs.more') }
  ];

  const infoValue = (label: string, value?: React.ReactNode) => (
    <div className="patient-profile-info">
      <span style={{ color: theme.muted }}>{label}</span>
      <strong>{value || '—'}</strong>
    </div>
  );

  const overviewCard = (title: string, content: React.ReactNode, action?: { label: string; tab: TabId }) => (
    <section className="patient-overview-card" style={theme.card}>
      <div className="patient-overview-card__heading">
        <h3>{title}</h3>
        {action && <button type="button" onClick={() => setActiveTab(action.tab)} style={{ color: theme.primary }}>{action.label}</button>}
      </div>
      {content}
    </section>
  );

  return (
    <div className="patient-profile-page" style={{ color: theme.text }}>
      <header className="patient-profile-header" style={theme.card}>
        <button type="button" className="patient-profile-back" onClick={onBack} style={theme.secondaryButton}>
          ← {t('patientProfile.back')}
        </button>
        <div className="patient-profile-identity">
          {patient.profilePicturePath ? (
            <img src={profilePictureUrl(patient.profilePicturePath)} alt={displayName} />
          ) : (
            <div className="patient-profile-avatar" style={{ background: theme.subtle, color: theme.muted }}>
              {displayName.slice(0, 1).toUpperCase()}
            </div>
          )}
          <div>
            <div className="patient-profile-title-row">
              <h1>{displayName}</h1>
              <span className="patient-profile-status" style={statusStyle}>{t(`status.${patient.status.toLowerCase()}`)}</span>
            </div>
            <div className="patient-profile-contact" style={{ color: theme.muted }}>
              <span>{age === null ? t('patientProfile.ageUnknown') : t('patientProfile.ageValue', { age })}</span>
              <span>{patient.phoneNumber || t('patientProfile.noPhone')}</span>
              <span>{patient.email || t('patientProfile.noEmail')}</span>
            </div>
          </div>
        </div>
        <div className="patient-profile-controls">
          <label>
            <span style={{ color: theme.muted }}>{t('patientProfile.labels.hc')}</span>
            <div>
              <input value={hcDraft} onChange={event => setHcDraft(event.target.value)} style={theme.input} />
              <button type="button" onClick={handleHcSave} disabled={hcSaving || hcDraft.trim() === (patient.hc || '')} style={theme.button}>
                {hcSaving ? t('patientProfile.hc.saving') : t('patientProfile.hc.save')}
              </button>
            </div>
          </label>
          <label>
            <span style={{ color: theme.muted }}>{t('patientProfile.labels.mainDoctor')}</span>
            <select value={patient.mainDoctorId || ''} onChange={event => handleMainDoctorChange(event.target.value)} style={theme.input}>
              <option value="">{t('common.auto', 'Auto (Inferred)')}</option>
              {doctors.map(doctor => <option key={doctor.id} value={doctor.id}>{doctor.firstName} {doctor.lastName}</option>)}
            </select>
          </label>
        </div>
        {hcMessage && <div className="patient-profile-message" style={{ color: theme.muted }}>{hcMessage}</div>}
        {hasCriticalAllergy && (
          <div className="patient-profile-critical">
            <strong>{t('patientProfile.criticalAllergy')}</strong>
            <span>{derived.allergies.filter(item => item.dangerLevel === 'Critical').map(item => item.name).join(', ')}</span>
          </div>
        )}
      </header>

      <nav className="patient-profile-tabs" style={{ borderColor: theme.border }} aria-label={t('patientProfile.navigation')}>
        {tabLabels.map(tab => (
          <button
            key={tab.id}
            type="button"
            aria-current={activeTab === tab.id ? 'page' : undefined}
            onClick={() => setActiveTab(tab.id)}
            style={{
              color: activeTab === tab.id ? theme.text : theme.muted,
              borderColor: activeTab === tab.id ? theme.primary : 'transparent'
            }}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {error && <div className="patient-profile-error">{error}</div>}

      {activeTab === 'overview' && (
        <div>
          {overviewWarnings.length > 0 && (
            <div className="patient-profile-warning">
              {t('patientProfile.partialData', { sections: overviewWarnings.join(', ') })}
              <button type="button" onClick={loadOverview}>{t('common.retry', 'Retry')}</button>
            </div>
          )}
          {overviewLoading ? <div>{t('patientProfile.loadingOverview')}</div> : (
            <>
              <div className="patient-profile-metrics">
                <div style={theme.card}>
                  <span style={{ color: theme.muted }}>{t('patientProfile.metrics.balance')}</span>
                  <strong className={derived.balanceDue > 0.005 ? 'metric-warning' : 'metric-good'}>Bs {derived.balanceDue.toFixed(2)}</strong>
                </div>
                <div style={theme.card}>
                  <span style={{ color: theme.muted }}>{t('patientProfile.metrics.pastDue')}</span>
                  <strong className={derived.pastDue > 0.005 ? 'metric-danger' : 'metric-good'}>Bs {derived.pastDue.toFixed(2)}</strong>
                </div>
                <div style={theme.card}>
                  <span style={{ color: theme.muted }}>{t('patientProfile.metrics.nextAppointment')}</span>
                  <strong>{formatDateTime(derived.nextAppointment?.startTime)}</strong>
                </div>
                <div style={theme.card}>
                  <span style={{ color: theme.muted }}>{t('patientProfile.metrics.lastVisit')}</span>
                  <strong>{formatDateTime(derived.lastVisit?.startTime)}</strong>
                </div>
              </div>

              <div className="patient-overview-grid">
                {overviewCard(
                  t('patientProfile.sections.basic'),
                  <div className="patient-profile-basic-grid">
                    {infoValue(t('patientProfile.labels.dob'), formatDate(patient.dateOfBirth))}
                    {infoValue(t('patientProfile.labels.address'), patient.address)}
                    {infoValue(t('patientProfile.labels.referral'), patient.referralSource)}
                    {infoValue(t('patientProfile.labels.nextContact'), formatDate(patient.nextContact))}
                    {infoValue(t('patientProfile.labels.mainDoctor'), mainDoctor ? `${mainDoctor.firstName} ${mainDoctor.lastName}` : t('common.auto', 'Auto (Inferred)'))}
                    {infoValue(t('patientProfile.labels.responsible'), patient.responsiblePartyId)}
                  </div>
                )}
                {overviewCard(
                  t('patientProfile.sections.allergies'),
                  derived.allergies.length ? (
                    <div className="patient-allergy-summary">
                      {derived.allergies.map(allergy => (
                        <div key={allergy.id}>
                          <span className={`allergy-level allergy-level--${allergy.dangerLevel.toLowerCase()}`}>{t(`allergies.levels.${allergy.dangerLevel}`)}</span>
                          <strong>{allergy.name}</strong>
                          <small style={{ color: theme.muted }}>{allergy.medications.join(', ')}</small>
                        </div>
                      ))}
                    </div>
                  ) : <div className="patient-overview-empty" style={{ color: theme.muted }}>{t('patientProfile.noAllergies')}</div>,
                  { label: t('patientProfile.viewAll'), tab: 'clinical' }
                )}
                {overviewCard(
                  t('patientProfile.sections.activeTreatments'),
                  derived.activeTreatments.length ? (
                    <div className="patient-treatment-summary">
                      {derived.activeTreatments.slice(0, 3).map(treatment => (
                        <div key={treatment.id}>
                          <div><strong>{treatment.name}</strong><span style={{ color: theme.muted }}>{treatment.status}</span></div>
                          <div>
                            <span>{t('patientProfile.paid')}: Bs {treatment.totalPaid.toFixed(2)}</span>
                            <span className={treatment.totalDue > 0.005 ? 'metric-warning' : 'metric-good'}>{t('patientProfile.due')}: Bs {treatment.totalDue.toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <div className="patient-overview-empty" style={{ color: theme.muted }}>{t('patientProfile.noActiveTreatments')}</div>,
                  { label: t('patientProfile.viewAll'), tab: 'treatments' }
                )}
                {overviewCard(
                  t('patientProfile.sections.upcoming'),
                  derived.nextAppointment ? (
                    <div className="patient-next-appointment">
                      <strong>{getAppointmentReason(derived.nextAppointment) || t('appointments.noReason')}</strong>
                      <span>{formatDateTime(derived.nextAppointment.startTime)}</span>
                    </div>
                  ) : <div className="patient-overview-empty" style={{ color: theme.muted }}>{t('patientProfile.noUpcoming')}</div>,
                  { label: t('patientProfile.viewAll'), tab: 'appointments' }
                )}
                {overviewCard(
                  t('patientProfile.sections.notes'),
                  <div className="patient-general-notes">
                    {patient.notes || <span style={{ color: theme.muted }}>{t('patientProfile.noNotes')}</span>}
                  </div>,
                  { label: t('patientProfile.viewAll'), tab: 'clinical' }
                )}
                {overviewCard(
                  t('patientProfile.sections.recentClinicalNotes'),
                  derived.recentStories.length ? (
                    <div className="patient-story-summary">
                      {derived.recentStories.map(story => (
                        <div key={story.id}>
                          <span style={{ color: theme.muted }}>{formatDate(story.date)}</span>
                          <p>{story.description}</p>
                        </div>
                      ))}
                    </div>
                  ) : <div className="patient-overview-empty" style={{ color: theme.muted }}>{t('patientProfile.noClinicalNotes')}</div>,
                  { label: t('patientProfile.viewAll'), tab: 'clinical' }
                )}
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'appointments' && <AppointmentList token={token} patientId={id} />}
      {activeTab === 'treatments' && <TreatmentsPanel token={token} patientId={id} />}
      {activeTab === 'clinical' && (
        <div className="patient-profile-section-stack">
          <section style={theme.card}>
            <h2>{t('patientProfile.sections.generalNotes')}</h2>
            <div className="patient-general-notes">{patient.notes || <span style={{ color: theme.muted }}>{t('patientProfile.noNotes')}</span>}</div>
          </section>
          <section style={theme.card}><AllergyManagement patientId={id} token={token} /></section>
          <section style={theme.card}><MedicalStoryList patientId={id} token={token} /></section>
        </div>
      )}
      {activeTab === 'finance' && (
        <div className="patient-profile-section-stack">
          <div className="patient-profile-metrics patient-profile-metrics--finance">
            <div style={theme.card}>
              <span style={{ color: theme.muted }}>{t('patientProfile.metrics.balance')}</span>
              <strong className={derived.balanceDue > 0.005 ? 'metric-warning' : 'metric-good'}>Bs {derived.balanceDue.toFixed(2)}</strong>
            </div>
            <div style={theme.card}>
              <span style={{ color: theme.muted }}>{t('patientProfile.metrics.pastDue')}</span>
              <strong className={derived.pastDue > 0.005 ? 'metric-danger' : 'metric-good'}>Bs {derived.pastDue.toFixed(2)}</strong>
            </div>
          </div>
          <section style={theme.card}><PatientFinancePanel token={token} patientId={id} /></section>
        </div>
      )}
      {activeTab === 'more' && (
        <div className="patient-profile-section-stack">
          <section style={theme.card}>
            <h2>{t('patientProfile.sections.photos')}</h2>
            <PatientPhotoGallery patientId={id} token={token} />
          </section>
          <section style={theme.card}>
            <h2>{t('patientProfile.sections.familyLinks')}</h2>
            <PatientFamilyLinksPanel patientId={id} token={token} onResponsibleChanged={loadPatient} />
          </section>
        </div>
      )}
    </div>
  );
}
